import { ProductImages } from "@components/commerce/product/image/ProductImages";
import { Box } from "@ecco/ui/box";
import dynamic from "next/dynamic";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import ProductImagesThumbnails from "./ProductImagesThumbnails";
import type { ProductMedia } from "@lib/product/types";
import { useStore } from "@store/store";
import { FibblProvider, useFibblState } from "@components/fibbl/FibblProvider";
import { useLgBreakpoint } from "@ecco/ui/use-breakpoint";
import { useIsHydrated } from "@hooks/useIsHydrated";
import { ProductImageFrame } from "@ecco/ui/product-image-frame";
import { getImageAltTag } from "@lib/utils/attributesUtils";

const ProductImagesGallery = dynamic(
    () => import("@components/commerce/product/image/gallery/ProductImagesGallery"),
    { ssr: false }
);

const ProductImagesCarousel = dynamic(
    () => import("@components/commerce/product/image/ProductImagesCarousel"),
    { ssr: false }
);

type IProductImagesContainerProps = {
    media: ProductMedia[];
    altTagPattern?: string;
};

export const ProductImagesContainer: FC<IProductImagesContainerProps & { sku: string }> = ({
    media,
    altTagPattern,
    sku,
}) => {
    return (
        <FibblProvider sku={sku}>
            <ProductImagesContainerContent media={media} altTagPattern={altTagPattern} />
        </FibblProvider>
    );
};

const ProductImagesContainerContent: FC<IProductImagesContainerProps> = ({
    media,
    altTagPattern,
}) => {
    // @TODO: We should align with UX team about show more button/functionality, otherwise we need to clean up.
    const isGalleryOpen = useStore((state) => state.data.productImageGallery.isGalleryOpen);
    const setGalleryOpen = useStore((state) => state.setGalleryOpen);

    const imageIndex = useStore((state) => state.data.productImageGallery.galleryImageIndex);
    const setImageIndex = useStore((state) => state.setGalleryImageIndex);

    const onImageClick = useCallback(
        (imageIndex: number) => {
            setImageIndex(imageIndex);
            setGalleryOpen(true);
        },
        [setImageIndex, setGalleryOpen]
    );

    const onCloseGallery = useCallback(() => {
        setGalleryOpen(false);
    }, [setGalleryOpen]);

    const { isVtoActive } = useFibblState();
    const isDesktop = useLgBreakpoint();
    const isHydrated = useIsHydrated();

    // The carousel is loaded client-only (ssr:false) to keep Swiper off the desktop
    // bundle. On mobile the SSR grid is display:none, so we paint a static first image
    // until the carousel has mounted to avoid a blank LCP area.
    const [isCarouselReady, setIsCarouselReady] = useState(false);
    const onCarouselReady = useCallback(() => setIsCarouselReady(true), []);

    useEffect(() => {
        if (imageIndex >= media.length && media.length > 0) {
            setImageIndex(0);
        }
    }, [media.length, imageIndex, setImageIndex]);

    const firstMedia = media[0];
    // Mirror the grid's `isHydrated ? isDesktop : true` guard so server and first client
    // render agree (avoids a hydration mismatch); after hydration the placeholder only
    // stays on mobile until the carousel has mounted.
    const showMobileImagePlaceholder =
        firstMedia?.type === "image" && !isCarouselReady && (isHydrated ? !isDesktop : true);

    return (
        <>
            <Box
                display={["none", "none", "none", "block"]}
                zIndex={3}
                position="absolute"
                left={5}
                top={16}
                h="auto"
                w={12}
                pointerEvents={isVtoActive ? "none" : "auto"}
                filter={isVtoActive ? "brightness(0.4)" : undefined}
                transition="filter 0.3s ease 0.1s"
            >
                <ProductImagesThumbnails
                    media={media}
                    setImageIndex={setImageIndex}
                    imageIndex={imageIndex}
                    altTagPattern={altTagPattern}
                />
            </Box>
            <Box w="100%" aspectRatio={"4/5"} pos={"sticky"} top={0}>
                {(isHydrated ? isDesktop : true) && (
                    <ProductImages
                        media={media}
                        onImageClick={onImageClick}
                        imageIndex={imageIndex}
                        altTagPattern={altTagPattern}
                    />
                )}

                {showMobileImagePlaceholder && (
                    <Box
                        display={["block", "block", "block", "none"]}
                        position="absolute"
                        inset={0}
                    >
                        <ProductImageFrame
                            src={firstMedia.url}
                            alt={getImageAltTag(altTagPattern, firstMedia.label)}
                            priority
                            sizes={["100vw", "100vw", "100vw", "100vw", "1vw"]}
                        />
                    </Box>
                )}

                {!isDesktop && (
                    <ProductImagesCarousel
                        media={media}
                        onImageClick={onImageClick}
                        altTagPattern={altTagPattern}
                        imageIndex={imageIndex}
                        setImageIndex={setImageIndex}
                        onReady={onCarouselReady}
                    />
                )}
                {isGalleryOpen ? (
                    <ProductImagesGallery
                        media={media}
                        onClose={onCloseGallery}
                        setSelectedImageIndex={setImageIndex}
                        selectedImageIndex={imageIndex}
                        altTagPattern={altTagPattern}
                        imageIndex={imageIndex}
                    />
                ) : null}
            </Box>
        </>
    );
};

export default ProductImagesContainer;
